/**
 * Document Processing Utilities
 * Centralized logic for handling document processing status and URL resolution
 */

/**
 * Ensure a URL has https:// prefix
 * @param {string} url - URL to process
 * @returns {string} URL with https:// prefix
 */
function ensureHttps(url) {
  if (!url) return url;
  return url.startsWith('http') ? url : `https://${url}`;
}

/**
 * Get thumbnail URL for a document, considering processing status
 * @param {Object} content - Document content object
 * @param {Object} documentProcessingStatuses - All document processing statuses
 * @returns {string|null} Thumbnail URL or null
 */
export function getDocumentThumbnailUrl(content, documentProcessingStatuses = {}) {
  if (!content) return null;

  // Get processing status data
  const processingStatusData = documentProcessingStatuses[content.id];

  // If we have processing status data, check if we should use it
  if (processingStatusData) {
    // FIXED LOGIC: Use processing status URLs if:
    // 1. Document is actively processing, OR
    // 2. Document processing is completed and we have better URLs in processing status
    const isActivelyProcessing = processingStatusData.state === 'process_document' && processingStatusData.status !== 'completed';

    const isCompletedWithBetterUrls = processingStatusData.state === 'process_document' && processingStatusData.status === 'completed' && processingStatusData.placeholder_url;

    const shouldUseProcessingUrls = isActivelyProcessing || isCompletedWithBetterUrls;

    if (shouldUseProcessingUrls && processingStatusData.placeholder_url) {
      return ensureHttps(processingStatusData.placeholder_url);
    }
  }

  // Use permanent thumbnail URLs (optimized_url is now the real thumbnail)
  const thumbnailUrl = content.optimized_url || content.preview_url || content.placeholder_url;
  return thumbnailUrl ? ensureHttps(thumbnailUrl) : null;
}

/**
 * Get document file URL for actual document viewing/downloading
 * @param {Object} content - Document content object
 * @param {Object} documentProcessingStatuses - All document processing statuses
 * @returns {string|null} Document file URL or null
 */
export function getDocumentFileUrl(content, documentProcessingStatuses = {}) {
  if (!content) return null;

  // Check if we have processing status data and should use it
  const processingStatusData = documentProcessingStatuses[content.id];
  if (processingStatusData) {
    // FIXED LOGIC: Use processing status URLs if actively processing OR completed with better URLs
    const isActivelyProcessing = processingStatusData.state === 'process_document' && processingStatusData.status !== 'completed';

    const isCompletedWithBetterUrls = processingStatusData.state === 'process_document' && processingStatusData.status === 'completed' && processingStatusData.optimized_url;

    const shouldUseProcessingUrls = isActivelyProcessing || isCompletedWithBetterUrls;

    if (shouldUseProcessingUrls && processingStatusData.optimized_url) {
      return processingStatusData.optimized_url;
    }
  }

  // For downloads, always use the original document URL (content.url)
  // The extra_attributes.pdf_file_url is only for viewing converted PDFs, not downloading original files
  return content.url || null;
}

/**
 * Get document viewing URL (prioritizes PDF version for DOC/DOCX for better viewing experience)
 * @param {Object} content - Document content object
 * @param {Object} documentProcessingStatuses - All document processing statuses
 * @returns {string|null} Document viewing URL or null
 */
export function getDocumentViewingUrl(content, documentProcessingStatuses = {}) {
  if (!content) return null;

  // Check if we have processing status data and should use it
  const processingStatusData = documentProcessingStatuses[content.id];
  if (processingStatusData) {
    // FIXED LOGIC: Use processing status URLs if actively processing OR completed with better URLs
    const isActivelyProcessing = processingStatusData.state === 'process_document' && processingStatusData.status !== 'completed';

    const isCompletedWithBetterUrls = processingStatusData.state === 'process_document' && processingStatusData.status === 'completed' && processingStatusData.optimized_url;

    const shouldUseProcessingUrls = isActivelyProcessing || isCompletedWithBetterUrls;

    if (shouldUseProcessingUrls && processingStatusData.optimized_url) {
      return processingStatusData.optimized_url;
    }
  }

  // For viewing, prioritize PDF version for better browser compatibility (especially for DOC/DOCX)
  // but fallback to original document URL if no PDF version is available
  const viewingUrl = content.extra_attributes?.pdf_file_url || content.url;
  return viewingUrl || null;
}

/**
 * Get enhanced document content object with processing status URLs
 * @param {Object} content - Original document content object
 * @param {Object} documentProcessingStatuses - All document processing statuses
 * @returns {Object} Enhanced content object with updated URLs
 */
export function getEnhancedDocumentContent(content, documentProcessingStatuses = {}) {
  if (!content) return content;

  // Only enhance document content
  if (content.type !== 'document' && content.content_type !== 'document') {
    return content;
  }

  // Check if we have processing status data
  const processingStatusData = documentProcessingStatuses[content.id];

  // If no processing status data, use the new permanent URLs structure
  if (!processingStatusData) {
    return content; // Content now has proper optimized_url (thumbnail) and extra_attributes.pdf_file_url (document)
  }

  // ENHANCED LOGIC: Handle temporary URLs from processing status correctly
  const isActivelyProcessing = processingStatusData.state === 'process_document' && processingStatusData.status !== 'completed';

  const isCompletedWithBetterUrls =
    processingStatusData.state === 'process_document' && processingStatusData.status === 'completed' && (processingStatusData.placeholder_url || processingStatusData.optimized_url);

  // NEW: Handle temporary URLs when state reaches "process_document"
  // This is when server provides optimized_url and preview_url as temporary URLs for immediate display
  const hasTemporaryProcessingUrls = processingStatusData.state === 'process_document' && (processingStatusData.optimized_url || processingStatusData.placeholder_url);

  const shouldUseProcessingUrls = isActivelyProcessing || isCompletedWithBetterUrls || hasTemporaryProcessingUrls;

  // If we should use processing URLs, replace with processing status URLs
  if (shouldUseProcessingUrls) {
    const enhancedContent = { ...content };

    // Replace thumbnail URLs with processing ones if available
    if (processingStatusData.placeholder_url) {
      enhancedContent.preview_url = processingStatusData.placeholder_url;
      enhancedContent.placeholder_url = processingStatusData.placeholder_url;
      enhancedContent.optimized_url = processingStatusData.placeholder_url; // Override with processing thumbnail
    }

    // CRITICAL: Handle temporary document URLs when state is "process_document"
    if (processingStatusData.state === 'process_document' && processingStatusData.optimized_url) {
      // Use optimized_url from processing status as temporary document URL
      enhancedContent.url = processingStatusData.optimized_url;

      // Set up temporary extra_attributes structure for document viewing
      if (!enhancedContent.extra_attributes) {
        enhancedContent.extra_attributes = {};
      }

      // Use the processing optimized_url as temporary pdf_file_url for immediate document viewing
      // This enables immediate document preview while processing continues
      enhancedContent.extra_attributes.pdf_file_url = processingStatusData.optimized_url;
    } else if (processingStatusData.optimized_url && processingStatusData.status === 'completed') {
      // Use completed processing URLs normally
      enhancedContent.url = processingStatusData.optimized_url;
    }

    return enhancedContent;
  }

  // Otherwise, use the permanent URLs (optimized_url = thumbnail, extra_attributes.pdf_file_url = document)
  return content;
}

/**
 * Batch enhance multiple document content objects
 * @param {Array} contentArray - Array of content objects
 * @param {Object} documentProcessingStatuses - All document processing statuses
 * @returns {Array} Array of enhanced content objects
 */
export function enhanceDocumentContentBatch(contentArray, documentProcessingStatuses = {}) {
  if (!Array.isArray(contentArray)) return contentArray;

  return contentArray.map(content => getEnhancedDocumentContent(content, documentProcessingStatuses));
}

/**
 * Check if a document is currently processing
 * @param {string|number} documentId - Document content ID
 * @param {Array} uploads - Upload progress store uploads array
 * @param {Object} documentProcessingStatuses - Document processing statuses
 * @returns {boolean} True if document is processing
 */
export function isDocumentProcessing(documentId, uploads = [], documentProcessingStatuses = {}) {
  if (!documentId) return false;

  // FIRST: Check upload progress store for active document uploads
  // This is the PRIMARY source for document processing progress

  // Look for an active upload with matching realId (content ID)
  const activeDocumentUpload = uploads.find(upload => upload.realId === documentId && upload.contentType === 'document' && upload.status !== 'error');

  if (activeDocumentUpload) {
    // Document is processing if:
    // 1. File upload complete (100%) but processing not done
    // 2. State hasn't reached 'process_document' OR processing_status isn't 'completed'
    if (activeDocumentUpload.progress >= 100) {
      const isProcessingComplete = activeDocumentUpload.state === 'process_document' && activeDocumentUpload.processingStatusData?.processing_status === 'completed';
      return !isProcessingComplete;
    }

    // Still uploading the file
    return true;
  }

  // ADDITIONAL CHECK: If we don't find an exact match, check if there are ANY active document uploads
  // This handles the case where the lightbox is showing an old document while a new version is being uploaded
  const anyActiveDocumentUpload = uploads.find(
    upload =>
      upload.contentType === 'document' && upload.status !== 'error' && (upload.progress < 100 || upload.state !== 'process_document' || upload.processingStatusData?.processing_status !== 'completed')
  );

  if (anyActiveDocumentUpload) {
    // Return true to block document loading during any document processing
    // This prevents loading corrupted files when there's an ID mismatch
    return true;
  }

  // FALLBACK: Check documentProcessingStatuses prop (legacy system)
  const processingStatusData = documentId ? documentProcessingStatuses[documentId] : null;

  if (!processingStatusData) {
    // If no processing status data anywhere, assume not processing
    return false;
  }

  // Document is processing if it's not in the final "process_document" state
  // AND processing_status is not 'completed'
  const hasReachedProcessDocument = processingStatusData.state === 'process_document';
  const isProcessingComplete = processingStatusData.processing_status === 'completed';

  // Document is still processing if it hasn't reached process_document state
  // OR if it has reached that state but processing_status is not completed
  const stillProcessing = !hasReachedProcessDocument || !isProcessingComplete;
  return stillProcessing;
}

/**
 * Get processing message for a document
 * @param {string|number} documentId - Document content ID
 * @param {Array} uploads - Upload progress store uploads array
 * @param {Object} documentProcessingStatuses - Document processing statuses
 * @returns {string} Processing message
 */
export function getDocumentProcessingMessage(documentId, uploads = []) {
  if (!documentId) return 'Processing document...';

  // FIRST: Check upload progress store for detailed progress info
  const activeDocumentUpload = uploads.find(upload => upload.realId === documentId && upload.contentType === 'document' && upload.status !== 'error');

  if (activeDocumentUpload) {
    // Use detailed progress information from upload store
    const progress = Math.round(activeDocumentUpload.progress || 0);
    const state = activeDocumentUpload.state;

    if (progress < 100) {
      return 'Uploading document...';
    } else if (state === 'process_document') {
      return activeDocumentUpload.message || 'Generating preview...';
    } else {
      return activeDocumentUpload.message || 'Preparing document...';
    }
  }
}
